// src/features/fourlink-config/hooks/useConfigState.js

import { useState, useCallback, useMemo } from 'react';

export const useConfigState = (initialData) => {
  // Estados principales
  const [currentStep, setCurrentStep] = useState(1);
  const [currentBlock, setCurrentBlock] = useState(1);

  // Estados para el Bloque 1
  const [selectedTipologias, setSelectedTipologias] = useState([]);
  const [centros, setCentros] = useState([]);
  const [selectedEquipos, setSelectedEquipos] = useState([]);
  const [equiposConfig, setEquiposConfig] = useState({});
  const [selectedCategorias, setSelectedCategorias] = useState([]);
  const [selectedPlanes, setSelectedPlanes] = useState([]);
  const [selectedActivos, setSelectedActivos] = useState([]);
  const [activosConfig, setActivosConfig] = useState({});
  const [selectedTareas, setSelectedTareas] = useState([]);
  const [registrosActivosConfig, setRegistrosActivosConfig] = useState({});
  const [tareasConfiguracion, setTareasConfiguracion] = useState({});
  const [tareaFotos, setTareaFotos] = useState({});

  // Estados para datos custom
  const [customTipologias, setCustomTipologias] = useState([]);
  const [hiddenTipologias, setHiddenTipologias] = useState([]);
  const [customEquipos, setCustomEquipos] = useState([]);
  const [hiddenEquipos, setHiddenEquipos] = useState([]);
  const [hiddenPlanes, setHiddenPlanes] = useState([]);
  const [hiddenGruposProfesionales, setHiddenGruposProfesionales] = useState([]);
  const [hiddenProfesionalesPorGrupo, setHiddenProfesionalesPorGrupo] = useState({});
  const [hiddenCategoriasActivos, setHiddenCategoriasActivos] = useState([]);
  const [hiddenActivosPorCategoria, setHiddenActivosPorCategoria] = useState({});
  const [hiddenCategoriasTareas, setHiddenCategoriasTareas] = useState([]);
  const [hiddenTareasPorCategoria, setHiddenTareasPorCategoria] = useState({});
  const [customPlanes, setCustomPlanes] = useState([]);
  const [customCategoriasProfesionales, setCustomCategoriasProfesionales] = useState({});
  const [customCategoriasActivos, setCustomCategoriasActivos] = useState({});
  const [customCategoriasTareas, setCustomCategoriasTareas] = useState({});

  // Estados de colapso
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [collapsedCategoriasProfesionales, setCollapsedCategoriasProfesionales] = useState({});
  const [collapsedCategoriasActivos, setCollapsedCategoriasActivos] = useState({});

  // Estados para el Bloque 2
  const [tipologiaEquipoMatrix, setTipologiaEquipoMatrix] = useState({});
  const [tipologiaActivoMatrix, setTipologiaActivoMatrix] = useState({});
  const [tareasAsignacionMatrix, setTareasAsignacionMatrix] = useState({});
  const [showVisualPreview, setShowVisualPreview] = useState(false);

  // Datos disponibles combinados (base + custom)
  const allTipologias = useMemo(() => {
    const baseList = Array.isArray(initialData?.tipologias) ? initialData.tipologias : [];
    const customList = Array.isArray(customTipologias) ? customTipologias : [];
    const seen = new Set();
    const merged = [];

    [...baseList, ...customList].forEach((raw) => {
      const name = typeof raw === 'string' ? raw.trim() : '';
      if (!name) return;
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(name);
    });

    return merged;
  }, [initialData?.tipologias, customTipologias]);

  const tipologiasDisponibles = useMemo(() => {
    const hiddenSet = new Set(
      (hiddenTipologias || []).map((t) => (typeof t === 'string' ? t.toLowerCase() : '')),
    );
    return allTipologias.filter((tip) => {
      const key = typeof tip === 'string' ? tip.toLowerCase() : '';
      return key && !hiddenSet.has(key);
    });
  }, [allTipologias, hiddenTipologias]);

  const allEquipos = useMemo(() => {
    const baseList = Array.isArray(initialData?.equipos) ? initialData.equipos : [];
    const customList = Array.isArray(customEquipos) ? customEquipos : [];
    const seen = new Set();
    const merged = [];

    [...baseList, ...customList].forEach((raw) => {
      const name = typeof raw === 'string' ? raw.trim() : '';
      if (!name) return;
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(name);
    });

    return merged;
  }, [initialData?.equipos, customEquipos]);

  const equiposDisponibles = useMemo(() => {
    const hiddenSet = new Set(
      (hiddenEquipos || []).map((e) => (typeof e === 'string' ? e.toLowerCase() : '')),
    );
    return allEquipos.filter((eq) => {
      const key = typeof eq === 'string' ? eq.toLowerCase() : '';
      return key && !hiddenSet.has(key);
    });
  }, [allEquipos, hiddenEquipos]);

  const planesDisponibles = useMemo(() => {
    const baseList = Array.isArray(initialData?.planes) ? initialData.planes : [];
    const customList = Array.isArray(customPlanes) ? customPlanes : [];
    const hiddenSet = new Set(
      (hiddenPlanes || []).map((p) => (typeof p === 'string' ? p.toLowerCase() : '')),
    );
    const seen = new Set();
    const merged = [];

    [...baseList, ...customList].forEach((raw) => {
      const name = typeof raw === 'string' ? raw.trim() : '';
      if (!name) return;
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      if (hiddenSet.has(key)) {
        seen.add(key);
        return;
      }
      seen.add(key);
      merged.push(name);
    });

    return merged;
  }, [initialData?.planes, customPlanes, hiddenPlanes]);

  const categoriasProfesionalesDisponibles = useMemo(() => {
    const merged = {};

    Object.entries(initialData?.categoriasProfesionales || {}).forEach(([grupo, lista]) => {
      merged[grupo] = Array.isArray(lista) ? [...lista] : [];
    });

    Object.entries(customCategoriasProfesionales || {}).forEach(([grupo, lista]) => {
      const existing = Array.isArray(merged[grupo]) ? merged[grupo] : [];
      const customList = Array.isArray(lista) ? lista : [];
      const combined = [...existing, ...customList];
      const unique = Array.from(new Set(combined.map((p) => (typeof p === 'string' ? p.trim() : '')))).filter(Boolean);
      merged[grupo] = unique.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    });

    const hiddenGroups = new Set(
      (hiddenGruposProfesionales || []).map((g) => (typeof g === 'string' ? g.toLowerCase() : '')),
    );
    const result = {};

    Object.entries(merged).forEach(([grupo, lista]) => {
      const key = typeof grupo === 'string' ? grupo.toLowerCase() : '';
      if (key && hiddenGroups.has(key)) return;
      const hiddenSet = new Set(
        (hiddenProfesionalesPorGrupo[grupo] || []).map((p) => (typeof p === 'string' ? p.toLowerCase() : '')),
      );
      result[grupo] = (lista || []).filter((p) => {
        const pk = typeof p === 'string' ? p.toLowerCase() : '';
        return pk && !hiddenSet.has(pk);
      });
    });

    return result;
  }, [initialData?.categoriasProfesionales, customCategoriasProfesionales, hiddenGruposProfesionales, hiddenProfesionalesPorGrupo]);

  const activosPorCategoriaDisponibles = useMemo(() => {
    const merged = {};

    Object.entries(initialData?.activosPorCategoria || {}).forEach(([categoria, lista]) => {
      merged[categoria] = Array.isArray(lista) ? [...lista] : [];
    });

    Object.entries(customCategoriasActivos || {}).forEach(([categoria, lista]) => {
      const existing = Array.isArray(merged[categoria]) ? merged[categoria] : [];
      const customList = Array.isArray(lista) ? lista : [];
      const combined = [...existing, ...customList];
      const unique = Array.from(new Set(combined.map((a) => (typeof a === 'string' ? a.trim() : '')))).filter(Boolean);
      merged[categoria] = unique.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    });

    const hiddenCats = new Set(
      (hiddenCategoriasActivos || []).map((c) => (typeof c === 'string' ? c.toLowerCase() : '')),
    );

    const result = {};
    Object.entries(merged).forEach(([categoria, lista]) => {
      const catKey = typeof categoria === 'string' ? categoria.toLowerCase() : '';
      if (catKey && hiddenCats.has(catKey)) return;
      const hiddenSet = new Set(
        (hiddenActivosPorCategoria[categoria] || []).map((a) => (typeof a === 'string' ? a.toLowerCase() : '')),
      );
      result[categoria] = (lista || []).filter((a) => {
        const key = typeof a === 'string' ? a.toLowerCase() : '';
        return key && !hiddenSet.has(key);
      });
    });

    return result;
  }, [initialData?.activosPorCategoria, customCategoriasActivos, hiddenCategoriasActivos, hiddenActivosPorCategoria]);

  const tareasPorCategoriaDisponibles = useMemo(() => {
    const merged = {};

    Object.entries(initialData?.tareas || {}).forEach(([categoria, lista]) => {
      merged[categoria] = Array.isArray(lista) ? [...lista] : [];
    });

    Object.entries(customCategoriasTareas || {}).forEach(([categoria, lista]) => {
      const existing = Array.isArray(merged[categoria]) ? merged[categoria] : [];
      const customList = Array.isArray(lista) ? lista : [];
      const combined = [...existing, ...customList];
      const uniqueById = [];
      const seen = new Set();
      combined.forEach((item) => {
        if (!item) return;
        const id = item.id ? String(item.id) : '';
        const key = id || `name:${(item.name || item.nombre || '').toString().trim().toLowerCase()}`;
        if (!key || seen.has(key)) return;
        seen.add(key);
        uniqueById.push(item);
      });
      merged[categoria] = uniqueById;
    });

    const hiddenCats = new Set(
      (hiddenCategoriasTareas || []).map((c) => (typeof c === 'string' ? c.toLowerCase() : '')),
    );
    const result = {};

    Object.entries(merged).forEach(([categoria, lista]) => {
      const catKey = typeof categoria === 'string' ? categoria.toLowerCase() : '';
      if (catKey && hiddenCats.has(catKey)) return;
      const hiddenSet = new Set(
        (hiddenTareasPorCategoria[categoria] || []).map((t) => (typeof t === 'string' ? t.toLowerCase() : '')),
      );
      result[categoria] = (lista || []).filter((t) => {
        const key = (t.id ? String(t.id) : (t.name || t.nombre || '')).toString().trim().toLowerCase();
        return key && !hiddenSet.has(key);
      });
    });

    return result;
  }, [initialData?.tareas, customCategoriasTareas, hiddenCategoriasTareas, hiddenTareasPorCategoria]);

  // Funciones para toggle de selección
  const toggleSelection = useCallback((item, selectedArray, setSelectedArray) => {
    if (selectedArray.includes(item)) {
      setSelectedArray(selectedArray.filter(i => i !== item));
    } else {
      setSelectedArray([...selectedArray, item]);
    }
  }, []);

  const toggleTipologia = useCallback((tipologia) => {
    toggleSelection(tipologia, selectedTipologias, setSelectedTipologias);
  }, [selectedTipologias, toggleSelection]);

  const toggleEquipo = useCallback((equipo) => {
    if (selectedEquipos.includes(equipo)) {
      setSelectedEquipos(selectedEquipos.filter(e => e !== equipo));
      const newConfig = { ...equiposConfig };
      delete newConfig[equipo];
      setEquiposConfig(newConfig);
    } else {
      setSelectedEquipos([...selectedEquipos, equipo]);
      const initialQRs = Array.from({ length: 5 }, (_, i) => ({
        id: i + 1,
        name: `QR ${i + 1}`,
        active: i === 0
      }));
      setEquiposConfig({
        ...equiposConfig,
        [equipo]: { qrs: initialQRs }
      });
    }
  }, [selectedEquipos, equiposConfig]);

  const toggleCategoria = useCallback((categoria) => {
    toggleSelection(categoria, selectedCategorias, setSelectedCategorias);
  }, [selectedCategorias, toggleSelection]);

  const togglePlan = useCallback((plan) => {
    toggleSelection(plan, selectedPlanes, setSelectedPlanes);
  }, [selectedPlanes, toggleSelection]);

  const toggleActivo = useCallback((activo) => {
    toggleSelection(activo, selectedActivos, setSelectedActivos);
  }, [selectedActivos, toggleSelection]);

  const toggleTarea = useCallback((tareaId) => {
    toggleSelection(tareaId, selectedTareas, setSelectedTareas);
  }, [selectedTareas, toggleSelection]);

  // Funciones para agregar custom items
  const addCustomTipologia = useCallback((providedName) => {
    const input = typeof providedName === 'string'
      ? providedName
      : (typeof window !== 'undefined'
        ? window.prompt('Nombre de la nueva tipología de centro:')
        : null);
    const newName = input ? input.trim() : '';
    if (!newName) return false;

    const normalized = newName.toLowerCase();
    const exists = allTipologias.some(
      (tip) => typeof tip === 'string' && tip.trim().toLowerCase() === normalized,
    );
    if (exists) {
      alert('Esa tipología ya existe.');
      return false;
    }

    setCustomTipologias((prev) => {
      const next = [...prev, newName];
      return next
        .map((t) => (typeof t === 'string' ? t.trim() : ''))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    });

    setHiddenTipologias((prev) => prev.filter((h) => h.toLowerCase() !== normalized));

    return true;
  }, [allTipologias]);

  const addCustomEquipo = useCallback((providedName) => {
    const input = typeof providedName === 'string'
      ? providedName
      : (typeof window !== 'undefined'
        ? window.prompt('Nombre del nuevo equipo:')
        : null);
    const newName = input ? input.trim() : '';
    if (!newName) return false;

    const normalized = newName.toLowerCase();
    const exists = allEquipos.some(
      (eq) => typeof eq === 'string' && eq.trim().toLowerCase() === normalized,
    );
    if (exists) {
      alert('Ese equipo ya existe.');
      return false;
    }

    setCustomEquipos((prev) => {
      const next = [...prev, newName];
      return next
        .map((t) => (typeof t === 'string' ? t.trim() : ''))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    });

    setHiddenEquipos((prev) => prev.filter((h) => h.toLowerCase() !== normalized));

    setEquiposConfig((prev) => ({
      ...prev,
      [newName]: {
        qrs: Array.from({ length: 5 }).map((_, idx) => ({
          id: idx + 1,
          name: `QR ${idx + 1}`,
          active: idx === 0,
        })),
      },
    }));

    return true;
  }, [allEquipos]);

  const addCustomPlan = useCallback((providedName) => {
    const input = typeof providedName === 'string'
      ? providedName
      : (typeof window !== 'undefined'
        ? window.prompt('Nombre del nuevo plan:')
        : null);
    const newName = input ? input.trim() : '';
    if (!newName) return false;

    const exists = [...planesDisponibles, ...(hiddenPlanes || [])].some(
      (plan) => typeof plan === 'string' && plan.trim().toLowerCase() === newName.toLowerCase(),
    );
    if (exists) {
      alert('Ese plan ya existe.');
      return false;
    }

    setCustomPlanes((prev) => {
      const next = [...prev, newName];
      return next
        .map((p) => (typeof p === 'string' ? p.trim() : ''))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    });

    setHiddenPlanes((prev) => prev.filter((p) => p.toLowerCase() !== newName.toLowerCase()));

    return true;
  }, [planesDisponibles, hiddenPlanes]);

  const addCustomCategoriaActivo = useCallback((nombre) => {
    const newName = typeof nombre === 'string' ? nombre.trim() : '';
    if (!newName) return false;

    const exists = Object.keys(activosPorCategoriaDisponibles || {}).some(
      (cat) => cat.trim().toLowerCase() === newName.toLowerCase(),
    );
    if (exists) {
      alert('Esa categoría de activo ya existe.');
      return false;
    }

    setCustomCategoriasActivos((prev) => ({
      ...prev,
      [newName]: [],
    }));
    setCollapsedCategoriasActivos((prev) => ({
      ...prev,
      [newName]: true,
    }));
    setHiddenCategoriasActivos((prev) => prev.filter((c) => c.toLowerCase() !== newName.toLowerCase()));

    return true;
  }, [activosPorCategoriaDisponibles]);

  const addCustomActivoEnCategoria = useCallback((categoria, nombre) => {
    const catName = typeof categoria === 'string' ? categoria.trim() : '';
    const newName = typeof nombre === 'string' ? nombre.trim() : '';
    if (!catName || !newName) return false;

    const currentList = activosPorCategoriaDisponibles[catName] || [];
    const exists = currentList.some((item) => item.trim().toLowerCase() === newName.toLowerCase());
    if (exists) {
      alert('Ese activo ya existe en esta categoría.');
      return false;
    }

    setCustomCategoriasActivos((prev) => {
      const next = { ...prev };
      const existing = Array.isArray(next[catName]) ? next[catName] : [];
      next[catName] = [...existing, newName]
        .map((p) => (typeof p === 'string' ? p.trim() : ''))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
      return next;
    });
    setCollapsedCategoriasActivos((prev) => ({
      ...prev,
      [catName]: false,
    }));
    setHiddenActivosPorCategoria((prev) => {
      const next = { ...(prev || {}) };
      const list = Array.isArray(next[catName]) ? next[catName] : [];
      next[catName] = list.filter((a) => a.toLowerCase() !== newName.toLowerCase());
      return next;
    });

    return true;
  }, [activosPorCategoriaDisponibles]);

  const addCustomGrupoProfesional = useCallback((nombre) => {
    const newName = typeof nombre === 'string' ? nombre.trim() : '';
    if (!newName) return false;

    const exists = Object.keys(categoriasProfesionalesDisponibles || {}).some(
      (grp) => grp.trim().toLowerCase() === newName.toLowerCase(),
    );
    if (exists) {
      alert('Ese grupo ya existe.');
      return false;
    }

    setCustomCategoriasProfesionales((prev) => ({
      ...prev,
      [newName]: [],
    }));
    setCollapsedCategoriasProfesionales((prev) => ({
      ...prev,
      [newName]: true,
    }));
    setHiddenGruposProfesionales((prev) => prev.filter((g) => g.toLowerCase() !== newName.toLowerCase()));

    return true;
  }, [categoriasProfesionalesDisponibles]);

  const addCustomProfesionalAGrupo = useCallback((grupo, nombre) => {
    const groupName = typeof grupo === 'string' ? grupo.trim() : '';
    const newName = typeof nombre === 'string' ? nombre.trim() : '';
    if (!groupName || !newName) return false;

    const currentList = categoriasProfesionalesDisponibles[groupName] || [];
    const exists = currentList.some((item) => item.trim().toLowerCase() === newName.toLowerCase());
    if (exists) {
      alert('Esa categoría profesional ya existe en este grupo.');
      return false;
    }

    setCustomCategoriasProfesionales((prev) => {
      const next = { ...prev };
      const existing = Array.isArray(next[groupName]) ? next[groupName] : [];
      next[groupName] = [...existing, newName]
        .map((p) => (typeof p === 'string' ? p.trim() : ''))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
      return next;
    });
    setCollapsedCategoriasProfesionales((prev) => ({
      ...prev,
      [groupName]: false,
    }));
    setHiddenProfesionalesPorGrupo((prev) => {
      const next = { ...(prev || {}) };
      const list = Array.isArray(next[groupName]) ? next[groupName] : [];
      next[groupName] = list.filter((p) => p.toLowerCase() !== newName.toLowerCase());
      return next;
    });

    return true;
  }, [categoriasProfesionalesDisponibles]);

  const hideTipologias = useCallback((tipologiasToHide) => {
    const items = Array.isArray(tipologiasToHide) ? tipologiasToHide : [];
    const normalizedSet = new Set();
    const cleaned = [];

    items.forEach((item) => {
      const name = typeof item === 'string' ? item.trim() : '';
      if (!name) return;
      const key = name.toLowerCase();
      if (normalizedSet.has(key)) return;
      normalizedSet.add(key);
      cleaned.push(name);
    });

    if (cleaned.length === 0) return;

    setHiddenTipologias((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const existing = new Set(current.map((t) => (typeof t === 'string' ? t.toLowerCase() : '')));
      const merged = [...current];
      cleaned.forEach((name) => {
        const key = name.toLowerCase();
        if (!existing.has(key)) {
          merged.push(name);
          existing.add(key);
        }
      });
      return merged;
    });

    setSelectedTipologias((prev) =>
      prev.filter((tip) => !normalizedSet.has((typeof tip === 'string' ? tip : '').toLowerCase())),
    );
  }, []);

  const restoreTipologia = useCallback((tipologia) => {
    const key = typeof tipologia === 'string' ? tipologia.trim().toLowerCase() : '';
    if (!key) return;
    setHiddenTipologias((prev) => (Array.isArray(prev) ? prev.filter((t) => t.toLowerCase() !== key) : []));
  }, []);

  const hideEquipos = useCallback((equiposToHide) => {
    const items = Array.isArray(equiposToHide) ? equiposToHide : [];
    const normalizedSet = new Set();
    const cleaned = [];

    items.forEach((item) => {
      const name = typeof item === 'string' ? item.trim() : '';
      if (!name) return;
      const key = name.toLowerCase();
      if (normalizedSet.has(key)) return;
      normalizedSet.add(key);
      cleaned.push(name);
    });

    if (cleaned.length === 0) return;

    setHiddenEquipos((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const existing = new Set(current.map((t) => (typeof t === 'string' ? t.toLowerCase() : '')));
      const merged = [...current];
      cleaned.forEach((name) => {
        const key = name.toLowerCase();
        if (!existing.has(key)) {
          merged.push(name);
          existing.add(key);
        }
      });
      return merged;
    });

    setSelectedEquipos((prev) =>
      prev.filter((eq) => !normalizedSet.has((typeof eq === 'string' ? eq : '').toLowerCase())),
    );
  }, []);

  const restoreEquipo = useCallback((equipo) => {
    const key = typeof equipo === 'string' ? equipo.trim().toLowerCase() : '';
    if (!key) return;
    setHiddenEquipos((prev) => (Array.isArray(prev) ? prev.filter((t) => t.toLowerCase() !== key) : []));
  }, []);

  const hideGruposProfesionales = useCallback((grupos, catalogoProfesionales = {}) => {
    const items = Array.isArray(grupos) ? grupos : [];
    const normalizedSet = new Set();
    const cleaned = [];

    items.forEach((item) => {
      const name = typeof item === 'string' ? item.trim() : '';
      if (!name) return;
      const key = name.toLowerCase();
      if (normalizedSet.has(key)) return;
      normalizedSet.add(key);
      cleaned.push(name);
    });

    if (cleaned.length === 0) return;

    setHiddenGruposProfesionales((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const existing = new Set(current.map((t) => (typeof t === 'string' ? t.toLowerCase() : '')));
      const merged = [...current];
      cleaned.forEach((name) => {
        const key = name.toLowerCase();
        if (!existing.has(key)) {
          merged.push(name);
          existing.add(key);
        }
      });
      return merged;
    });

    const profesionalesAEliminar = new Set();
    Object.entries(catalogoProfesionales || {}).forEach(([grupo, lista]) => {
      if (!normalizedSet.has(grupo.toLowerCase())) return;
      (Array.isArray(lista) ? lista : []).forEach((p) => {
        const key = typeof p === 'string' ? p.toLowerCase() : '';
        if (key) profesionalesAEliminar.add(key);
      });
    });

    if (profesionalesAEliminar.size > 0) {
      setSelectedCategorias((prev) =>
        prev.filter((cat) => !profesionalesAEliminar.has((typeof cat === 'string' ? cat : '').toLowerCase())),
      );
    }
  }, [setSelectedCategorias]);

  const restoreGrupoProfesional = useCallback((grupo) => {
    const key = typeof grupo === 'string' ? grupo.trim().toLowerCase() : '';
    if (!key) return;
    setHiddenGruposProfesionales((prev) => (Array.isArray(prev) ? prev.filter((t) => t.toLowerCase() !== key) : []));
  }, []);

  const hideProfesionalesDeGrupo = useCallback((grupo, profesionales) => {
    const groupKey = typeof grupo === 'string' ? grupo.trim() : '';
    if (!groupKey) return;
    const items = Array.isArray(profesionales) ? profesionales : [];
    const normalizedSet = new Set();
    const cleaned = [];

    items.forEach((item) => {
      const name = typeof item === 'string' ? item.trim() : '';
      if (!name) return;
      const key = name.toLowerCase();
      if (normalizedSet.has(key)) return;
      normalizedSet.add(key);
      cleaned.push(name);
    });

    if (cleaned.length === 0) return;

    setHiddenProfesionalesPorGrupo((prev) => {
      const next = { ...(prev || {}) };
      const existing = Array.isArray(next[groupKey]) ? next[groupKey] : [];
      const existSet = new Set(existing.map((p) => (typeof p === 'string' ? p.toLowerCase() : '')));
      const merged = [...existing];
      cleaned.forEach((name) => {
        const key = name.toLowerCase();
        if (!existSet.has(key)) {
          merged.push(name);
          existSet.add(key);
        }
      });
      next[groupKey] = merged;
      return next;
    });

    setSelectedCategorias((prev) =>
      prev.filter((cat) => !normalizedSet.has((typeof cat === 'string' ? cat : '').toLowerCase())),
    );
  }, [setSelectedCategorias]);

  const restoreProfesionalDeGrupo = useCallback((grupo, profesional) => {
    const groupKey = typeof grupo === 'string' ? grupo.trim() : '';
    const key = typeof profesional === 'string' ? profesional.trim().toLowerCase() : '';
    if (!groupKey || !key) return;
    setHiddenProfesionalesPorGrupo((prev) => {
      const next = { ...(prev || {}) };
      const list = Array.isArray(next[groupKey]) ? next[groupKey] : [];
      next[groupKey] = list.filter((p) => p.toLowerCase() !== key);
      return next;
    });
  }, []);

  const hidePlanes = useCallback((planesToHide) => {
    const items = Array.isArray(planesToHide) ? planesToHide : [];
    const normalizedSet = new Set();
    const cleaned = [];

    items.forEach((item) => {
      const name = typeof item === 'string' ? item.trim() : '';
      if (!name) return;
      const key = name.toLowerCase();
      if (normalizedSet.has(key)) return;
      normalizedSet.add(key);
      cleaned.push(name);
    });

    if (cleaned.length === 0) return;

    setHiddenPlanes((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const existing = new Set(current.map((t) => (typeof t === 'string' ? t.toLowerCase() : '')));
      const merged = [...current];
      cleaned.forEach((name) => {
        const key = name.toLowerCase();
        if (!existing.has(key)) {
          merged.push(name);
          existing.add(key);
        }
      });
      return merged;
    });

    setSelectedPlanes((prev) =>
      prev.filter((plan) => !normalizedSet.has((typeof plan === 'string' ? plan : '').toLowerCase())),
    );
  }, []);

  const restorePlan = useCallback((plan) => {
    const key = typeof plan === 'string' ? plan.trim().toLowerCase() : '';
    if (!key) return;
    setHiddenPlanes((prev) => (Array.isArray(prev) ? prev.filter((p) => p.toLowerCase() !== key) : []));
  }, []);

  const hideCategoriasActivosFn = useCallback((categorias, catalogo = {}) => {
    const items = Array.isArray(categorias) ? categorias : [];
    const normalizedSet = new Set();
    const cleaned = [];

    items.forEach((item) => {
      const name = typeof item === 'string' ? item.trim() : '';
      if (!name) return;
      const key = name.toLowerCase();
      if (normalizedSet.has(key)) return;
      normalizedSet.add(key);
      cleaned.push(name);
    });

    if (cleaned.length === 0) return;

    setHiddenCategoriasActivos((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const existing = new Set(current.map((t) => (typeof t === 'string' ? t.toLowerCase() : '')));
      const merged = [...current];
      cleaned.forEach((name) => {
        const key = name.toLowerCase();
        if (!existing.has(key)) {
          merged.push(name);
          existing.add(key);
        }
      });
      return merged;
    });

    const activosAEliminar = new Set();
    Object.entries(catalogo || {}).forEach(([cat, lista]) => {
      if (!normalizedSet.has(cat.toLowerCase())) return;
      (Array.isArray(lista) ? lista : []).forEach((a) => {
        const key = typeof a === 'string' ? a.toLowerCase() : '';
        if (key) activosAEliminar.add(key);
      });
    });

    if (activosAEliminar.size > 0) {
      setSelectedActivos((prev) =>
        prev.filter((act) => !activosAEliminar.has((typeof act === 'string' ? act : '').toLowerCase())),
      );
    }
  }, [setSelectedActivos]);

  const restoreCategoriaActivo = useCallback((categoria) => {
    const key = typeof categoria === 'string' ? categoria.trim().toLowerCase() : '';
    if (!key) return;
    setHiddenCategoriasActivos((prev) => (Array.isArray(prev) ? prev.filter((c) => c.toLowerCase() !== key) : []));
  }, []);

  const hideActivosDeCategoria = useCallback((categoria, activos) => {
    const catName = typeof categoria === 'string' ? categoria.trim() : '';
    if (!catName) return;
    const items = Array.isArray(activos) ? activos : [];
    const normalizedSet = new Set();
    const cleaned = [];

    items.forEach((item) => {
      const name = typeof item === 'string' ? item.trim() : '';
      if (!name) return;
      const key = name.toLowerCase();
      if (normalizedSet.has(key)) return;
      normalizedSet.add(key);
      cleaned.push(name);
    });

    if (cleaned.length === 0) return;

    setHiddenActivosPorCategoria((prev) => {
      const next = { ...(prev || {}) };
      const existing = Array.isArray(next[catName]) ? next[catName] : [];
      const existSet = new Set(existing.map((p) => (typeof p === 'string' ? p.toLowerCase() : '')));
      const merged = [...existing];
      cleaned.forEach((name) => {
        const key = name.toLowerCase();
        if (!existSet.has(key)) {
          merged.push(name);
          existSet.add(key);
        }
      });
      next[catName] = merged;
      return next;
    });

    setSelectedActivos((prev) =>
      prev.filter((act) => !normalizedSet.has((typeof act === 'string' ? act : '').toLowerCase())),
    );
  }, [setSelectedActivos]);

  const restoreActivoDeCategoria = useCallback((categoria, activo) => {
    const catName = typeof categoria === 'string' ? categoria.trim() : '';
    const key = typeof activo === 'string' ? activo.trim().toLowerCase() : '';
    if (!catName || !key) return;
    setHiddenActivosPorCategoria((prev) => {
      const next = { ...(prev || {}) };
      const list = Array.isArray(next[catName]) ? next[catName] : [];
      next[catName] = list.filter((p) => p.toLowerCase() !== key);
      return next;
    });
  }, []);

  const addCustomCategoriaTarea = useCallback((nombre) => {
    const newName = typeof nombre === 'string' ? nombre.trim() : '';
    if (!newName) return false;

    const exists = Object.keys(tareasPorCategoriaDisponibles || {}).some(
      (cat) => cat.trim().toLowerCase() === newName.toLowerCase(),
    );
    if (exists) {
      alert('Esa categoría de tarea ya existe.');
      return false;
    }

    setCustomCategoriasTareas((prev) => ({
      ...prev,
      [newName]: [],
    }));
    setCollapsedCategories((prev) => ({
      ...prev,
      [newName]: true,
    }));
    setHiddenCategoriasTareas((prev) => prev.filter((c) => c.toLowerCase() !== newName.toLowerCase()));

    return true;
  }, [tareasPorCategoriaDisponibles]);

  const addCustomTareaEnCategoria = useCallback((categoria, nombre) => {
    const catName = typeof categoria === 'string' ? categoria.trim() : '';
    const newName = typeof nombre === 'string' ? nombre.trim() : '';
    if (!catName || !newName) return false;

    const currentList = tareasPorCategoriaDisponibles[catName] || [];
    const exists = currentList.some((item) => {
      const key = (item.id ? String(item.id) : (item.name || item.nombre || '')).toString().trim().toLowerCase();
      return key === newName.toLowerCase();
    });
    if (exists) {
      alert('Esa tarea ya existe en esta categoría.');
      return false;
    }

    const newTarea = {
      id: `custom-${Date.now()}`,
      name: newName,
      type: 'ninguno',
      periodicidad: 'ninguna',
      dias_semana: '',
      checklists: [],
      orden: Number.POSITIVE_INFINITY,
    };

    setCustomCategoriasTareas((prev) => {
      const next = { ...prev };
      const existing = Array.isArray(next[catName]) ? next[catName] : [];
      next[catName] = [...existing, newTarea];
      return next;
    });
    setCollapsedCategories((prev) => ({
      ...prev,
      [catName]: false,
    }));
    setHiddenTareasPorCategoria((prev) => {
      const next = { ...(prev || {}) };
      const list = Array.isArray(next[catName]) ? next[catName] : [];
      next[catName] = list.filter((t) => t.toLowerCase() !== newName.toLowerCase());
      return next;
    });

    return true;
  }, [tareasPorCategoriaDisponibles]);

  const hideCategoriasTareasFn = useCallback((categorias, catalogo = {}) => {
    const items = Array.isArray(categorias) ? categorias : [];
    const normalizedSet = new Set();
    const cleaned = [];

    items.forEach((item) => {
      const name = typeof item === 'string' ? item.trim() : '';
      if (!name) return;
      const key = name.toLowerCase();
      if (normalizedSet.has(key)) return;
      normalizedSet.add(key);
      cleaned.push(name);
    });

    if (cleaned.length === 0) return;

    setHiddenCategoriasTareas((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const existing = new Set(current.map((t) => (typeof t === 'string' ? t.toLowerCase() : '')));
      const merged = [...current];
      cleaned.forEach((name) => {
        const key = name.toLowerCase();
        if (!existing.has(key)) {
          merged.push(name);
          existing.add(key);
        }
      });
      return merged;
    });

    const tareasAEliminar = new Set();
    Object.entries(catalogo || {}).forEach(([cat, lista]) => {
      if (!normalizedSet.has(cat.toLowerCase())) return;
      (Array.isArray(lista) ? lista : []).forEach((t) => {
        const key = (t.id ? String(t.id) : (t.name || t.nombre || '')).toString().trim().toLowerCase();
        if (key) tareasAEliminar.add(key);
      });
    });

    if (tareasAEliminar.size > 0) {
      setSelectedTareas((prev) =>
        prev.filter((tareaId) => !tareasAEliminar.has(String(tareaId).trim().toLowerCase())),
      );
    }
  }, [setSelectedTareas]);

  const restoreCategoriaTarea = useCallback((categoria) => {
    const key = typeof categoria === 'string' ? categoria.trim().toLowerCase() : '';
    if (!key) return;
    setHiddenCategoriasTareas((prev) => (Array.isArray(prev) ? prev.filter((c) => c.toLowerCase() !== key) : []));
  }, []);

  const hideTareasDeCategoria = useCallback((categoria, tareas) => {
    const catName = typeof categoria === 'string' ? categoria.trim() : '';
    if (!catName) return;
    const items = Array.isArray(tareas) ? tareas : [];
    const normalizedSet = new Set();
    const cleaned = [];

    items.forEach((item) => {
      const name = typeof item === 'string' ? item.trim() : '';
      if (!name) return;
      const key = name.toLowerCase();
      if (normalizedSet.has(key)) return;
      normalizedSet.add(key);
      cleaned.push(name);
    });

    if (cleaned.length === 0) return;

    setHiddenTareasPorCategoria((prev) => {
      const next = { ...(prev || {}) };
      const existing = Array.isArray(next[catName]) ? next[catName] : [];
      const existSet = new Set(existing.map((p) => (typeof p === 'string' ? p.toLowerCase() : '')));
      const merged = [...existing];
      cleaned.forEach((name) => {
        const key = name.toLowerCase();
        if (!existSet.has(key)) {
          merged.push(name);
          existSet.add(key);
        }
      });
      next[catName] = merged;
      return next;
    });

    setSelectedTareas((prev) =>
      prev.filter((t) => !normalizedSet.has(String(t).trim().toLowerCase())),
    );
  }, [setSelectedTareas]);

  const restoreTareaDeCategoria = useCallback((categoria, tareaName) => {
    const catName = typeof categoria === 'string' ? categoria.trim() : '';
    const key = typeof tareaName === 'string' ? tareaName.trim().toLowerCase() : '';
    if (!catName || !key) return;
    setHiddenTareasPorCategoria((prev) => {
      const next = { ...(prev || {}) };
      const list = Array.isArray(next[catName]) ? next[catName] : [];
      next[catName] = list.filter((p) => p.toLowerCase() !== key);
      return next;
    });
  }, []);

  // Funciones para gestión de centros
  const addCentro = useCallback((centro) => {
    setCentros((prev) => [...prev, centro]);
  }, []);

  const deleteCentro = useCallback((id) => {
    setCentros((prev) => prev.filter(centro => centro.id !== id));
  }, []);

  // Funciones para gestión de QRs
  const updateQRName = useCallback((equipo, qrIndex, newName) => {
    const currentConfig = equiposConfig[equipo];
    if (currentConfig && currentConfig.qrs) {
      const updatedQRs = [...currentConfig.qrs];
      
      let finalName = newName;
      if (!finalName.startsWith('QR ')) {
        if (finalName.toLowerCase().startsWith('qr ')) {
          finalName = 'QR ' + finalName.substring(3);
        } else if (finalName.toLowerCase().startsWith('qr')) {
          finalName = 'QR ' + finalName.substring(2);
        } else {
          finalName = 'QR ' + finalName;
        }
      }
      
      const otherQRs = updatedQRs.filter((_, index) => index !== qrIndex);
      const isDuplicate = otherQRs.some(qr => qr.name.toLowerCase() === finalName.toLowerCase());
      
      if (isDuplicate) {
        let counter = 2;
        let uniqueName = finalName;
        while (otherQRs.some(qr => qr.name.toLowerCase() === uniqueName.toLowerCase())) {
          uniqueName = `${finalName} ${counter}`;
          counter++;
        }
        finalName = uniqueName;
      }
      
      updatedQRs[qrIndex] = { ...updatedQRs[qrIndex], name: finalName };
      
      setEquiposConfig({
        ...equiposConfig,
        [equipo]: { qrs: updatedQRs }
      });
    }
  }, [equiposConfig]);

  const toggleQRActive = useCallback((equipo, qrIndex) => {
    const currentConfig = equiposConfig[equipo];
    if (currentConfig && currentConfig.qrs) {
      const updatedQRs = [...currentConfig.qrs];
      updatedQRs[qrIndex] = { 
        ...updatedQRs[qrIndex], 
        active: !updatedQRs[qrIndex].active 
      };
      
      setEquiposConfig({
        ...equiposConfig,
        [equipo]: { qrs: updatedQRs }
      });
    }
  }, [equiposConfig]);

  // Funciones para configuración de activos
  const updateActivoConfig = useCallback((activo, field, value) => {
    setActivosConfig((prev) => ({
      ...prev,
      [activo]: {
        ...prev[activo],
        [field]: value
      }
    }));
  }, []);

  // Funciones para matrices
  const toggleTipologiaEquipoSelection = useCallback((tipologia, equipo, qrId) => {
    const key = `${tipologia}-${equipo}`;
    setTipologiaEquipoMatrix(prev => {
      const current = prev[key] || [];
      const isSelected = current.includes(qrId);
      
      return {
        ...prev,
        [key]: isSelected 
          ? current.filter(id => id !== qrId)
          : [...current, qrId]
      };
    });
  }, []);

  const toggleTipologiaActivoSelection = useCallback((tipologia, activo) => {
    const key = `${tipologia}-${activo}`;
    setTipologiaActivoMatrix(prev => {
      const isSelected = prev[key] || false;
      return {
        ...prev,
        [key]: !isSelected
      };
    });
  }, []);

  const toggleTareaAsignacion = useCallback((tareaId, tipologia, equipo, qrId) => {
    const key = `${tareaId}-${tipologia}-${equipo}-${qrId}`;
    const isCurrentlyChecked = tareasAsignacionMatrix[key] || false;
    
    const isFirstTipologia = selectedTipologias.indexOf(tipologia) === 0;
    
    if (isFirstTipologia) {
      const newMatrix = { ...tareasAsignacionMatrix };
      newMatrix[key] = !isCurrentlyChecked;
      
      selectedTipologias.slice(1).forEach(otherTipologia => {
        const otherKey = `${tareaId}-${otherTipologia}-${equipo}-${qrId}`;
        
        const otherTipologiaCombinations = [];
        selectedEquipos.forEach(eq => {
          const selectedQRs = tipologiaEquipoMatrix[`${otherTipologia}-${eq}`] || [];
          const equipoQRs = equiposConfig[eq]?.qrs || [];
          
          selectedQRs.forEach(qId => {
            const qr = equipoQRs.find(q => q.id === qId && q.active);
            if (qr) {
              otherTipologiaCombinations.push({
                equipo: eq,
                qrId: qId
              });
            }
          });
        });
        
        const combinationExists = otherTipologiaCombinations.some(
          combo => combo.equipo === equipo && combo.qrId === qrId
        );
        
        if (combinationExists) {
          newMatrix[otherKey] = !isCurrentlyChecked;
        }
      });
      
      setTareasAsignacionMatrix(newMatrix);
    } else {
      setTareasAsignacionMatrix(prev => ({
        ...prev,
        [key]: !isCurrentlyChecked
      }));
    }
  }, [selectedTipologias, selectedEquipos, tipologiaEquipoMatrix, equiposConfig, tareasAsignacionMatrix]);

  const hydrateFromSnapshot = useCallback((snapshot) => {
    if (!snapshot || typeof snapshot !== 'object') return;

    if (Number.isFinite(snapshot.currentStep)) setCurrentStep(snapshot.currentStep);
    if (Number.isFinite(snapshot.currentBlock)) setCurrentBlock(snapshot.currentBlock);

    const hiddenTipologiasSet = Array.isArray(snapshot.hiddenTipologias)
      ? new Set(snapshot.hiddenTipologias.map((t) => (typeof t === 'string' ? t.toLowerCase() : '')))
      : null;

    if (Array.isArray(snapshot.selectedTipologias)) {
      const filtered = hiddenTipologiasSet
        ? snapshot.selectedTipologias.filter(
            (tip) => !hiddenTipologiasSet.has((typeof tip === 'string' ? tip : '').toLowerCase()),
          )
        : snapshot.selectedTipologias;
      setSelectedTipologias(filtered);
    }
    if (Array.isArray(snapshot.centros)) setCentros(snapshot.centros);
    if (Array.isArray(snapshot.selectedEquipos)) setSelectedEquipos(snapshot.selectedEquipos);
    if (snapshot.equiposConfig && typeof snapshot.equiposConfig === 'object') setEquiposConfig(snapshot.equiposConfig);
    if (Array.isArray(snapshot.selectedCategorias)) setSelectedCategorias(snapshot.selectedCategorias);
    if (Array.isArray(snapshot.selectedPlanes)) setSelectedPlanes(snapshot.selectedPlanes);
    if (Array.isArray(snapshot.selectedActivos)) setSelectedActivos(snapshot.selectedActivos);
    if (snapshot.activosConfig && typeof snapshot.activosConfig === 'object') setActivosConfig(snapshot.activosConfig);
    if (Array.isArray(snapshot.selectedTareas)) setSelectedTareas(snapshot.selectedTareas);
    if (snapshot.registrosActivosConfig && typeof snapshot.registrosActivosConfig === 'object') {
      setRegistrosActivosConfig(snapshot.registrosActivosConfig);
    }
    if (snapshot.tareasConfiguracion && typeof snapshot.tareasConfiguracion === 'object') {
      setTareasConfiguracion(snapshot.tareasConfiguracion);
    }
    if (snapshot.tareaFotos && typeof snapshot.tareaFotos === 'object') setTareaFotos(snapshot.tareaFotos);

    if (Array.isArray(snapshot.customTipologias)) setCustomTipologias(snapshot.customTipologias);
    if (Array.isArray(snapshot.hiddenTipologias)) setHiddenTipologias(snapshot.hiddenTipologias);
    if (Array.isArray(snapshot.customEquipos)) setCustomEquipos(snapshot.customEquipos);
    if (Array.isArray(snapshot.hiddenEquipos)) setHiddenEquipos(snapshot.hiddenEquipos);
    if (Array.isArray(snapshot.hiddenGruposProfesionales)) setHiddenGruposProfesionales(snapshot.hiddenGruposProfesionales);
    if (snapshot.hiddenProfesionalesPorGrupo && typeof snapshot.hiddenProfesionalesPorGrupo === 'object') {
      setHiddenProfesionalesPorGrupo(snapshot.hiddenProfesionalesPorGrupo);
    }
    if (Array.isArray(snapshot.customPlanes)) setCustomPlanes(snapshot.customPlanes);
    if (snapshot.customCategoriasProfesionales && typeof snapshot.customCategoriasProfesionales === 'object') {
      setCustomCategoriasProfesionales(snapshot.customCategoriasProfesionales);
    }
    if (snapshot.customCategoriasActivos && typeof snapshot.customCategoriasActivos === 'object') {
      setCustomCategoriasActivos(snapshot.customCategoriasActivos);
    }
    if (snapshot.customCategoriasTareas && typeof snapshot.customCategoriasTareas === 'object') {
      setCustomCategoriasTareas(snapshot.customCategoriasTareas);
    }
    if (Array.isArray(snapshot.hiddenPlanes)) setHiddenPlanes(snapshot.hiddenPlanes);
    if (Array.isArray(snapshot.hiddenCategoriasActivos)) setHiddenCategoriasActivos(snapshot.hiddenCategoriasActivos);
    if (snapshot.hiddenActivosPorCategoria && typeof snapshot.hiddenActivosPorCategoria === 'object') {
      setHiddenActivosPorCategoria(snapshot.hiddenActivosPorCategoria);
    }
    if (Array.isArray(snapshot.hiddenCategoriasTareas)) setHiddenCategoriasTareas(snapshot.hiddenCategoriasTareas);
    if (snapshot.hiddenTareasPorCategoria && typeof snapshot.hiddenTareasPorCategoria === 'object') {
      setHiddenTareasPorCategoria(snapshot.hiddenTareasPorCategoria);
    }

    if (snapshot.collapsedCategories && typeof snapshot.collapsedCategories === 'object') {
      setCollapsedCategories(snapshot.collapsedCategories);
    }
    if (snapshot.collapsedCategoriasProfesionales && typeof snapshot.collapsedCategoriasProfesionales === 'object') {
      setCollapsedCategoriasProfesionales(snapshot.collapsedCategoriasProfesionales);
    }
    if (snapshot.collapsedCategoriasActivos && typeof snapshot.collapsedCategoriasActivos === 'object') {
      setCollapsedCategoriasActivos(snapshot.collapsedCategoriasActivos);
    }

    if (snapshot.tipologiaEquipoMatrix && typeof snapshot.tipologiaEquipoMatrix === 'object') {
      setTipologiaEquipoMatrix(snapshot.tipologiaEquipoMatrix);
    }
    if (snapshot.tipologiaActivoMatrix && typeof snapshot.tipologiaActivoMatrix === 'object') {
      setTipologiaActivoMatrix(snapshot.tipologiaActivoMatrix);
    }
    if (snapshot.tareasAsignacionMatrix && typeof snapshot.tareasAsignacionMatrix === 'object') {
      setTareasAsignacionMatrix(snapshot.tareasAsignacionMatrix);
    }
    if (typeof snapshot.showVisualPreview === 'boolean') setShowVisualPreview(snapshot.showVisualPreview);
  }, []);

  return {
    // Estados
    currentStep,
    setCurrentStep,
    currentBlock,
    setCurrentBlock,
    selectedTipologias,
    setSelectedTipologias,
    centros,
    setCentros,
    selectedEquipos,
    setSelectedEquipos,
    equiposConfig,
    setEquiposConfig,
    selectedCategorias,
    setSelectedCategorias,
    selectedPlanes,
    setSelectedPlanes,
    selectedActivos,
    setSelectedActivos,
    activosConfig,
    setActivosConfig,
    selectedTareas,
    setSelectedTareas,
    registrosActivosConfig,
    setRegistrosActivosConfig,
    tareasConfiguracion,
    setTareasConfiguracion,
    tareaFotos,
    setTareaFotos,
    hiddenTipologias,
    setHiddenTipologias,
    hiddenEquipos,
    setHiddenEquipos,
    hiddenPlanes,
    setHiddenPlanes,
    hiddenGruposProfesionales,
    setHiddenGruposProfesionales,
    hiddenProfesionalesPorGrupo,
    setHiddenProfesionalesPorGrupo,
    hiddenCategoriasActivos,
    setHiddenCategoriasActivos,
    hiddenActivosPorCategoria,
    setHiddenActivosPorCategoria,
    hiddenCategoriasTareas,
    setHiddenCategoriasTareas,
    hiddenTareasPorCategoria,
    setHiddenTareasPorCategoria,
    customTipologias,
    setCustomTipologias,
    customEquipos,
    setCustomEquipos,
    customPlanes,
    setCustomPlanes,
    customCategoriasProfesionales,
    setCustomCategoriasProfesionales,
    customCategoriasActivos,
    setCustomCategoriasActivos,
    customCategoriasTareas,
    setCustomCategoriasTareas,
    collapsedCategories,
    setCollapsedCategories,
    collapsedCategoriasProfesionales,
    setCollapsedCategoriasProfesionales,
    collapsedCategoriasActivos,
    setCollapsedCategoriasActivos,
    tipologiaEquipoMatrix,
    setTipologiaEquipoMatrix,
    tipologiaActivoMatrix,
    setTipologiaActivoMatrix,
    tareasAsignacionMatrix,
    setTareasAsignacionMatrix,
    showVisualPreview,
    setShowVisualPreview,
    hydrateFromSnapshot,

    // Datos disponibles
    allTipologias,
    tipologiasDisponibles,
    allEquipos,
    equiposDisponibles,
    planesDisponibles,
    categoriasProfesionalesDisponibles,
    activosPorCategoriaDisponibles,
    tareasPorCategoriaDisponibles,

    // Funciones
    toggleTipologia,
    toggleEquipo,
    toggleCategoria,
    togglePlan,
    toggleActivo,
    toggleTarea,
    addCustomTipologia,
    addCustomEquipo,
    addCustomPlan,
    addCustomCategoriaActivo,
    addCustomActivoEnCategoria,
    hidePlanes,
    restorePlan,
    addCustomGrupoProfesional,
    addCustomProfesionalAGrupo,
    hideTipologias,
    restoreTipologia,
    hideEquipos,
    restoreEquipo,
    hideGruposProfesionales,
    restoreGrupoProfesional,
    hideProfesionalesDeGrupo,
    restoreProfesionalDeGrupo,
    hideCategoriasActivosFn,
    restoreCategoriaActivo,
    hideActivosDeCategoria,
    restoreActivoDeCategoria,
    addCustomCategoriaTarea,
    addCustomTareaEnCategoria,
    hideCategoriasTareasFn,
    restoreCategoriaTarea,
    hideTareasDeCategoria,
    restoreTareaDeCategoria,
    addCentro,
    deleteCentro,
    updateQRName,
    toggleQRActive,
    updateActivoConfig,
    toggleTipologiaEquipoSelection,
    toggleTipologiaActivoSelection,
    toggleTareaAsignacion,
  };
};
